import {
  Component,
  DoCheck,
  AfterViewInit,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { IFormField } from "../../../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { animate, keyframes, style, transition, trigger } from "@angular/animations";
import { Comment, CommentAudit, CommentBrief, CommentService, FileSystemService } from "../../../../../../swaggerServices";
import { ICommentField, PastedDocMetadata, IQuestionFocus } from "../subcategory";
import { Subscription } from "rxjs";
import { UntypedFormControl, FormGroup } from "@angular/forms";
import { IDocumentMetadata } from "../../model/i-tax-return";
import { Tenant } from "../../../../../shared/types/tenant.enum";
import { ConfigStateService } from "../../../../config/config-state.service";
import { AttachmentsService, Folders } from "./attachments/attachments.service";
import { CommentValue } from "./attachments/attachments.component";
import { AttachmentRef } from "../../urls";

declare var $: any;

export interface IComment extends Omit<Comment, "attachments">, CommentBrief {
  groupId?: number;
  subGroupId?: number;
  id?: number;
  question?: string;
  author?: string;
  role?: string;
  date?: string; // string date
  dateMoment?: any; // moment date
  isReply?: boolean;
  commentFields?: ICommentField[];
  message?: string;
  comments?: IComment[]; // list of replies to comment
  replyComment?: CommentValue; // reply comment
  replyTo?: boolean; // whether to show textbox
  user?: any; // author of comment
  commentAudits?: CommentAudit[];
  answerWarningId?: number;
  answerFlagId?: number;
  createdAtString?: string;
  parts: { text?: string, url?: string, docId?: string, image?: string, imageName?: string }[];
  documents?: IDocumentMetadata[];
  attachments?: AttachmentRef[];
  hasSentToClient?: boolean;
  externalUrl?: string;
}

@Component({
    selector: "app-comments",
    templateUrl: "./comments.component.html",
    styleUrls: ["./comments.component.scss"],
    // flip animation for edit field labels
    // https://github.com/daneden/animate.css/tree/master/source/flippers
    animations: [
        trigger("fieldLabel", [
            transition(":enter", animate(2000, keyframes([
                style({ transform: "perspective(400px) rotate3d(1, 0, 0, 90deg)", offset: 0 }),
                style({ transform: "perspective(400px) rotate3d(1, 0, 0, -20deg)", offset: 0.25 }),
                style({ transform: "perspective(400px) rotate3d(1, 0, 0, 10deg)", offset: 0.5 }),
                style({ transform: "perspective(400px) rotate3d(1, 0, 0, -5deg)", offset: 1 }),
            ]))),
            transition(":leave", animate(500, keyframes([
                style({ transform: "perspective(400px) rotate3d(1, 0, 0, -20deg)", offset: 0.3 }),
                style({ transform: "perspective(400px) rotate3d(1, 0, 0, 90deg)", offset: 1 }),
            ]))),
        ]),
    ],
    standalone: false
})
export class CommentsComponent implements OnInit, OnDestroy, DoCheck, AfterViewInit {
  @Input() comment: UntypedFormControl; // textbox comment
  @Input() comments: IComment[];
  @Input() activeGroup?: any; // current selected group tab
  @Input() activeSubgroup?: any;
  @Input() subCategory?: any;
  @Input() commentFields: ICommentField[]; // list of fields to comment on
  @Input() questions: IFormField[]; // list of full questions to filter from
  @Input() isSaving: boolean;
  @Input() readonly: boolean;
  @Input() getCommentMeta: Function;
  @Input() handlePostCommentReply: Function;
  @Input() pastedDocMetadatas: PastedDocMetadata[];
  @Input() documents: IDocumentMetadata[];
  @Input() isSmartForm?: boolean = false;
  @Input() activeCategory?: any;
  @Input() smarFormWithCount?: any;
  @Input() hiddenAndMandatoryMap?: any;
  @Input() rootFolder?: string;

  @Output() totalComments: EventEmitter<number> = new EventEmitter<number>(); // total comments count for selected group or subgroup
  @Output() commentSave: EventEmitter<any> = new EventEmitter<any>();
  @Output() commentReply: EventEmitter<any> = new EventEmitter<any>();
  @Output() deleteCommentField: EventEmitter<ICommentField> = new EventEmitter<ICommentField>();
  @Output() focusOnQuestion : EventEmitter<IQuestionFocus> = new EventEmitter<IQuestionFocus>();
  @Output() setModal: EventEmitter<string> = new EventEmitter<string>();

  commentsFiltered: IComment[] = []; // list of filtered comments for group or subgroup
  isSavingReply: boolean; // when posting a comment reply
  showQuestionId: boolean;
  subscriptions: Subscription[] = [];
  isDragOver: boolean = false;
  attachments: Map<string, File> = new Map();
  modalImgSrc: string | null = null;
  folders: Folders = {
    rootFolder: "",
    attachmentsFolder: "",
    targetFolder: "",
  };

  constructor(
    private commentService: CommentService,
    private config: ConfigStateService,
    private attachmentsService: AttachmentsService,
  ) {
  }

  ngOnInit() {
    this.showQuestionId = this.config.tenant !== Tenant.AU;
    this.attachmentsService.setRootFolder(this.folders, this.rootFolder);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  setfocusOnQuestionChildComp (questionId: string, type: 'warning' | 'comment' | 'flag', answerId: any){
    const questionFocus: IQuestionFocus = { id: questionId, type , answerId};
    this.focusOnQuestion.emit(questionFocus);
  }


  // submit a comment reply
  async saveCommentReply(comment: IComment) {

    this.isSavingReply = true;

    const { attachments, success } = await this.attachmentsService.uploadAttachments(this.folders, this.subCategory.name, Array.from(comment.replyComment?.attachments.values() ?? []));
    if (!success) {
      this.isSavingReply = false;
      return;
    };

    const _comment: any = {
      parentCommentId: comment.id,
      message: comment.replyComment.message,
      attachments,
      answerIds: []
    };

    this.subscriptions.push(this.commentService.addComment(_comment).subscribe((response: any) => {
      const newComment = response.comment;
      if (newComment && newComment.commentAudits && newComment.commentAudits.length) {
        newComment.commentAudits[0].user = response.user;
      }
      this.handlePostCommentReply(newComment);

      this.isSavingReply = false;
      comment.replyTo = false;
      comment.replyComment = { message: "", attachments: new Map() };

      this.commentReply.emit();
    },
      () => {
        this.isSavingReply = false;
      }));

  }

  // re-filter comments on change
  ngDoCheck() {
    this.filterComments();
    this.updateCommentCount();
  }

  ngAfterViewInit() {
     this.scrollToBottom();
  }

  updateCommentCount() {
    let totalComments = 0;
    // count total comments
    totalComments = this.commentsFiltered.length;

    // include comment replies in total count
    this.commentsFiltered.forEach((comment: IComment) => totalComments += comment.comments.length);
    this.totalComments.emit(totalComments);
  }

  // sends event emitter to subcategory component to save all answers then saves comment
  saveComment() {
    this.attachments = new Map()
    this.commentSave.emit(this.comments.length);
  }


  // filters list of comments belonging to a group or subgroup in focus
  // order top level comments and replies by date asc
  filterComments() {
    if (!this.isSmartForm) {
      // when a group is in focus, filter any comment that belongs to group
      if (this.comments.length > 0 && this.activeGroup) {

        this.commentsFiltered = this.comments.filter((comment: IComment) => {
          if (comment.commentFields.length > 0)
            return comment.commentFields.some((item: ICommentField) => item.groupId === this.activeGroup.id);
          else
            return comment.groupId === this.activeGroup.id;
        });

        // when a subgroup is in focus, filter only comments that contain labels which exist in subgroup
        if (this.activeGroup.activeSubGroup) {
          this.commentsFiltered = this.commentsFiltered.filter((comment: IComment) => {
            return comment.commentFields.some((commentLabel: ICommentField) => {
              return comment.commentFields.length > 0 && commentLabel.subGroupId === this.activeGroup.activeSubGroup.id;
            });
          });

          // when a table row is in focus
          if (this.activeGroup.activeSubGroup.activeRow != null)
            this.commentsFiltered = this.commentsFiltered.filter((comment: IComment) =>
              comment.commentFields.some((commentLabel: ICommentField) =>
                commentLabel.rowIndex === this.activeGroup.activeSubGroup.activeRow));
        }
      }
    }else {
      this.commentsFiltered = this.comments.filter((comment: IComment) => comment.commentFields.length > 0);

      if (this.activeCategory) {
        this.commentsFiltered = this.commentsFiltered.filter((comment: IComment) => {
          return comment.commentFields.some((commentLabel: ICommentField) => {
            return comment.commentFields.length > 0 && commentLabel.smartFormCategoryId === this.activeCategory.id;
          });
        })
      }
    }
  }

  private scrollToBottom() {
    const container = document.querySelector('#comments-list-container');
    container.scrollTop = container.scrollHeight;
  }

  cancelReply(comment: IComment) {
    comment.replyTo = false;
    comment.replyComment = { message: "", attachments: new Map() };
  }
}
